import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

export const TRAINER_CATALOG_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const trainerCatalogReadQuerySchema = z.object({
  orgId: optionalQueryString,
  includeArchived: optionalQueryString,
  search: optionalQueryString,
}).passthrough()

export const trainerCatalogReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  auditWritten: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  r2Touched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const trainerCourseTypeSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string(),
  canonicalKey: z.string(),
  displayOrder: z.number().finite(),
  compatibilityAliases: z.array(z.string()),
  active: z.boolean(),
  deprecated: z.boolean(),
  deprecatedAt: z.string().nullable(),
  status: z.enum(['active', 'deprecated', 'archived']),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerCustomerSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const trainerCatalogReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.union([
    z.tuple([z.literal('trainer_course_types')]),
    z.tuple([z.literal('trainer_customers')]),
  ]),
  projection: z.enum([
    'trainer-course-type-catalog',
    'trainer-course-type-admin-catalog',
    'trainer-customer-catalog',
  ]),
  rawPayloadReturned: z.literal(false),
  includeArchived: z.boolean().optional(),
}).passthrough()

export const trainerCourseTypeListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CATALOG_CONTRACT_VERSION),
  courseTypes: z.array(trainerCourseTypeSummarySchema),
  catalogEvidence: trainerCatalogReadEvidenceSchema.extend({
    tablesRead: z.tuple([z.literal('trainer_course_types')]),
    projection: z.enum(['trainer-course-type-catalog', 'trainer-course-type-admin-catalog']),
    includeArchived: z.boolean(),
  }).passthrough(),
  sideEffects: trainerCatalogReadSideEffectsSchema,
}).passthrough()

export const trainerCustomerListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CATALOG_CONTRACT_VERSION),
  customers: z.array(trainerCustomerSummarySchema),
  catalogEvidence: trainerCatalogReadEvidenceSchema.extend({
    tablesRead: z.tuple([z.literal('trainer_customers')]),
    projection: z.literal('trainer-customer-catalog'),
  }).passthrough(),
  sideEffects: trainerCatalogReadSideEffectsSchema,
}).passthrough()

export type TrainerCatalogReadQuery = z.infer<typeof trainerCatalogReadQuerySchema>
export type TrainerCatalogReadSideEffects = z.infer<typeof trainerCatalogReadSideEffectsSchema>
export type TrainerCourseTypeSummary = z.infer<typeof trainerCourseTypeSummarySchema>
export type TrainerCustomerSummary = z.infer<typeof trainerCustomerSummarySchema>
export type TrainerCatalogReadEvidence = z.infer<typeof trainerCatalogReadEvidenceSchema>
export type TrainerCourseTypeListResponse = z.infer<typeof trainerCourseTypeListResponseSchema>
export type TrainerCustomerListResponse = z.infer<typeof trainerCustomerListResponseSchema>
